import { canonicalTwinBoundaryJson } from "./twin-boundary-json.js";
export { canonicalTwinBoundaryJson } from "./twin-boundary-json.js";
import { z } from "zod";
import { authoringSlugSchema, authoringTenantSlugSchema } from "./authoring-shared.js";
import { archivedTwinQuerySemanticsSchema } from "./twin-query-semantics.js";
const boundedText = (maximum) => z.string().trim().min(1).max(maximum);
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const organisationIdSchema = boundedText(200);
const identifierSchema = boundedText(200);
const uniqueStrings = (values) => new Set(values).size === values.length;
export const twinLogicScopeSchema = z.object({
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
}).strict();
export const twinLogicExploratoryAccessSchema = z.enum([
    "open",
    "discouraged",
    "blocked",
]);
export const twinLogicSemanticSelectionMethodSchema = z.enum([
    "explicit_pin",
    "deterministic_match",
    "model_selected",
]);
const boundedUniqueTextArray = (maximumItems, maximumLength) => z.array(boundedText(maximumLength)).max(maximumItems).refine(uniqueStrings, "Values must be unique.").transform((values) => [...values].sort());
export const twinLogicRoutingCardSchema = z.object({
    description: boundedText(4_000),
    aliases: boundedUniqueTextArray(32, 120),
    activationPhrases: boundedUniqueTextArray(64, 240),
    supportedIntents: boundedUniqueTextArray(64, 500).refine((values) => values.length >= 1, "At least one supported intent is required."),
    unsupportedIntents: boundedUniqueTextArray(64, 500),
}).strict().superRefine((card, context) => {
    const supported = new Set(card.supportedIntents.map((value) => value.toLowerCase()));
    if (card.unsupportedIntents.some((value) => supported.has(value.toLowerCase()))) {
        context.addIssue({
            code: "custom",
            path: ["unsupportedIntents"],
            message: "Supported and unsupported intents must not overlap.",
        });
    }
});
export const twinLogicFieldTypeSchema = z.enum([
    "string",
    "integer",
    "number",
    "boolean",
    "date",
    "timestamp",
    "json",
    "unknown",
]);
export const twinLogicAggregationSchema = z.enum([
    "dimension",
    "sum",
    "count",
    "count_distinct",
    "average",
    "minimum",
    "maximum",
    "none",
    "unknown",
]);
export const twinLogicAdditivitySchema = z.enum([
    "additive",
    "semi_additive",
    "non_additive",
    "not_applicable",
    "unknown",
]);
export const twinLogicFieldSemanticSchema = z.object({
    field: z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u),
    meaning: boundedText(2_000),
    type: twinLogicFieldTypeSchema,
    aggregation: twinLogicAggregationSchema,
    additivity: twinLogicAdditivitySchema,
    dateRole: boundedText(120),
    unit: boundedText(120),
    currency: boundedText(120),
}).strict();
export const twinLogicSemanticsSchema = z.object({
    population: boundedText(4_000),
    exclusions: boundedUniqueTextArray(64, 1_000),
    grain: boundedText(2_000),
    fields: z.array(twinLogicFieldSemanticSchema).min(1).max(256),
    dateRoles: boundedUniqueTextArray(32, 1_000),
    fiscalRules: boundedUniqueTextArray(32, 1_000),
    completeness: boundedText(4_000),
    freshness: boundedText(4_000),
}).strict().superRefine((semantics, context) => {
    const fields = semantics.fields.map(({ field }) => field);
    if (!uniqueStrings(fields)) {
        context.addIssue({
            code: "custom",
            path: ["fields"],
            message: "Semantic field names must be unique.",
        });
    }
});
export const twinLogicQueryOperationSchema = z.enum([
    "select",
    "filter",
    "group_by",
    "aggregate",
    "sort",
    "limit",
    "compare",
    "time_series",
]);
export const twinLogicQueryPolicySchema = z.object({
    allowedOperations: z.array(twinLogicQueryOperationSchema).min(1).max(8).refine(uniqueStrings, "Allowed operations must be unique.").transform((operations) => [...operations].sort()),
    allowedFilters: z.array(z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u))
        .max(256).refine(uniqueStrings, "Allowed filters must be unique.")
        .transform((filters) => [...filters].sort()),
    blockedOperations: z.array(twinLogicQueryOperationSchema).max(8).refine(uniqueStrings, "Blocked operations must be unique.").transform((operations) => [...operations].sort()),
}).strict().superRefine((policy, context) => {
    const allowed = new Set(policy.allowedOperations);
    if (policy.blockedOperations.some((operation) => allowed.has(operation))) {
        context.addIssue({
            code: "custom",
            path: ["blockedOperations"],
            message: "An operation cannot be both allowed and blocked.",
        });
    }
});
export const twinLogicSourceRelationSchema = z.object({
    schema: z.enum(["connector_warehouse", "mlx_curated"]),
    relation: z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u),
}).strict();
export const twinLogicRelationBindingSchema = z.object({
    schema: z.literal("mlx_curated"),
    relation: z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u),
    definitionSha256: sha256Schema,
    columnSignatureSha256: sha256Schema,
    lineageSha256: sha256Schema,
    sourceRelations: z.array(twinLogicSourceRelationSchema).min(1).max(128)
        .transform((relations) => [...relations].sort((left, right) => {
        const leftKey = `${left.schema}.${left.relation}`;
        const rightKey = `${right.schema}.${right.relation}`;
        return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
    })),
}).strict().superRefine((binding, context) => {
    const references = binding.sourceRelations.map(({ schema, relation }) => `${schema}.${relation}`);
    if (!uniqueStrings(references)) {
        context.addIssue({
            code: "custom",
            path: ["sourceRelations"],
            message: "Source relations must be unique.",
        });
    }
});
export const twinLogicEvaluationReferenceSchema = z.object({
    kind: z.string().regex(/^[a-z][a-z0-9_]{0,79}$/u),
    id: identifierSchema,
    sha256: sha256Schema,
}).strict();
export const twinLogicProductLifecycleSchema = z.object({
    capabilityKey: authoringSlugSchema,
    catalogVisibility: z.enum(["public", "supporting"]),
    supersedesProductSlugs: boundedUniqueTextArray(64, 80),
    dependencyProductSlugs: boundedUniqueTextArray(64, 80),
    /** Live products this one is a filtered copy of, for the reviewer; absent when none. */
    narrowsProductSlugs: boundedUniqueTextArray(64, 80).optional(),
}).strict().superRefine((lifecycle, context) => {
    for (const [field, values] of [
        ["supersedesProductSlugs", lifecycle.supersedesProductSlugs],
        ["dependencyProductSlugs", lifecycle.dependencyProductSlugs],
        ["narrowsProductSlugs", lifecycle.narrowsProductSlugs ?? []],
    ]) {
        for (const value of values) {
            if (!authoringSlugSchema.safeParse(value).success) {
                context.addIssue({ code: "custom", path: [field], message: "Invalid product slug." });
            }
        }
    }
});
export const twinLogicProductVersionV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-logic.product-version.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    productSlug: authoringSlugSchema,
    productVersion: z.number().int().positive().max(2_147_483_647),
    source: z.object({
        authoringRequestSha256: sha256Schema,
        sourceProjectionSha256: sha256Schema,
        verifiedAuthoringReleaseId: identifierSchema,
        verifiedAuthoringReleaseSha256: sha256Schema,
        acceptedDecisionId: identifierSchema,
        acceptedDecisionSha256: sha256Schema,
        automatedAuthoringClosureSha256: sha256Schema,
        semanticMemberLedgerSha256: sha256Schema,
    }).strict(),
    warehouseScope: z.object({
        organisationIds: boundedUniqueTextArray(256, 200).refine((values) => values.length >= 1, "At least one warehouse organisation id is required."),
        rowPredicateSha256: sha256Schema,
    }).strict(),
    lifecycle: twinLogicProductLifecycleSchema.optional(),
    routingCard: twinLogicRoutingCardSchema,
    semantics: twinLogicSemanticsSchema,
    // Absence is historical unknown evidence; parsing must not insert new bytes.
    querySemantics: archivedTwinQuerySemanticsSchema.optional(),
    queryPolicy: twinLogicQueryPolicySchema,
    relationBinding: twinLogicRelationBindingSchema,
    evaluationRefs: z.array(twinLogicEvaluationReferenceSchema).min(1).max(64)
        .transform((references) => [...references].sort((left, right) => {
        const leftKey = `${left.kind}\0${left.id}`;
        const rightKey = `${right.kind}\0${right.id}`;
        return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
    })),
    exploratoryAccess: twinLogicExploratoryAccessSchema,
}).strict().superRefine((version, context) => {
    const fields = new Set(version.semantics.fields.map(({ field }) => field));
    if (version.querySemantics && (version.querySemantics.fields.length !== fields.size ||
        version.querySemantics.fields.some(({ field }) => !fields.has(field)))) {
        context.addIssue({ code: "custom", path: ["querySemantics"],
            message: "Query semantics must describe exactly the product's semantic fields." });
    }
    for (const filter of version.queryPolicy.allowedFilters) {
        if (!fields.has(filter)) {
            context.addIssue({
                code: "custom",
                path: ["queryPolicy", "allowedFilters"],
                message: `Allowed filter '${filter}' is not a declared semantic field.`,
            });
        }
    }
    const refs = version.evaluationRefs.map(({ kind, id }) => `${kind}\0${id}`);
    if (version.lifecycle !== undefined
        && (version.lifecycle.supersedesProductSlugs.includes(version.productSlug)
            || version.lifecycle.dependencyProductSlugs.includes(version.productSlug)
            || (version.lifecycle.narrowsProductSlugs ?? []).includes(version.productSlug))) {
        context.addIssue({
            code: "custom",
            path: ["lifecycle"],
            message: "A product lifecycle cannot reference its own slug.",
        });
    }
    if (!uniqueStrings(refs)) {
        context.addIssue({
            code: "custom",
            path: ["evaluationRefs"],
            message: "Evaluation references must be unique by kind and id.",
        });
    }
});
export const twinLogicAdmissionEvidenceV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-logic.admission-evidence.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    productSlug: authoringSlugSchema,
    productVersion: z.number().int().positive().max(2_147_483_647),
    productVersionSha256: sha256Schema,
    policySha256: sha256Schema,
    admissionBuildSha256: sha256Schema,
    checks: z.array(z.object({
        code: z.enum([
            "verified_release_binding",
            "automated_accepted_decision",
            "required_semantics_known",
            "row_free_release",
            "relation_definition",
            "column_signature",
            "lineage",
            "product_audits",
            "relationship_product_family",
            "policy_build",
        ]),
        status: z.literal("passed"),
        evidenceSha256: sha256Schema,
    }).strict()).min(1).max(128).transform((checks) => [...checks].sort((left, right) => left.code < right.code ? -1 : left.code > right.code ? 1 : 0)),
    admittedBy: boundedText(200),
    admittedAt: z.number().int().positive().safe(),
}).strict().superRefine((evidence, context) => {
    const codes = evidence.checks.map(({ code }) => code);
    if (!uniqueStrings(codes) || codes.length !== 10) {
        context.addIssue({
            code: "custom",
            path: ["checks"],
            message: "Admission evidence must contain every required check exactly once.",
        });
    }
    const digests = new Map(evidence.checks.map(({ code, evidenceSha256 }) => [code, evidenceSha256]));
    if (digests.get("required_semantics_known") !== evidence.productVersionSha256
        || digests.get("policy_build") !== evidence.admissionBuildSha256) {
        context.addIssue({
            code: "custom",
            path: ["checks"],
            message: "Admission checks must bind the product version and admission build.",
        });
    }
});
export const twinLogicInstallationV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-logic.installation.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    productId: z.string().regex(/^tl_prod_[0-9a-f]{32}$/u),
    productVersionId: z.string().regex(/^tl_pver_[0-9a-f]{32}$/u),
    productVersionSha256: sha256Schema,
    relation: z.object({
        schema: z.literal("mlx_curated"),
        relation: z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u),
        definitionSha256: sha256Schema,
        columnSignatureSha256: sha256Schema,
        lineageSha256: sha256Schema,
    }).strict(),
    verification: z.object({
        verifierBuildSha256: sha256Schema,
        observedDefinitionSha256: sha256Schema,
        observedColumnSignatureSha256: sha256Schema,
        observedLineageSha256: sha256Schema,
        verifiedAt: z.number().int().positive().safe(),
    }).strict(),
    installedBy: boundedText(200),
    installedAt: z.number().int().positive().safe(),
}).strict().superRefine((installation, context) => {
    const expected = installation.relation;
    const observed = installation.verification;
    if (expected.definitionSha256 !== observed.observedDefinitionSha256
        || expected.columnSignatureSha256 !== observed.observedColumnSignatureSha256
        || expected.lineageSha256 !== observed.observedLineageSha256) {
        context.addIssue({
            code: "custom",
            path: ["verification"],
            message: "Observed installation hashes must exactly match the product relation binding.",
        });
    }
});
export const twinLogicWarehouseVisibilityReceiptV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-logic.warehouse-visibility.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    productId: z.string().regex(/^tl_prod_[0-9a-f]{32}$/u),
    productVersionId: z.string().regex(/^tl_pver_[0-9a-f]{32}$/u),
    installationId: z.string().regex(/^tl_inst_[0-9a-f]{32}$/u),
    transition: z.enum(["activated", "deactivated"]),
    visibility: z.enum(["serving", "hidden"]),
    warehouseOrganisationIds: boundedUniqueTextArray(256, 200).refine((values) => values.length >= 1, "At least one warehouse organisation id is required."),
    runtimeReadRole: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,62}$/u),
    relation: z.object({
        schema: z.literal("mlx_curated"),
        relation: z.string().regex(/^[a-z_][a-z0-9_]{0,126}$/u),
        definitionSha256: sha256Schema,
        columnSignatureSha256: sha256Schema,
        lineageSha256: sha256Schema,
        sourceRelations: z.array(twinLogicSourceRelationSchema).min(1).max(128)
            .transform((relations) => [...relations].sort((left, right) => {
            const leftKey = `${left.schema}.${left.relation}`;
            const rightKey = `${right.schema}.${right.relation}`;
            return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
        })),
    }).strict(),
    verification: z.object({
        verifierBuildSha256: sha256Schema,
        observedDefinitionSha256: sha256Schema,
        observedColumnSignatureSha256: sha256Schema,
        observedLineageSha256: sha256Schema,
        observedSourceRelations: z.array(twinLogicSourceRelationSchema).min(1).max(128)
            .transform((relations) => [...relations].sort((left, right) => {
            const leftKey = `${left.schema}.${left.relation}`;
            const rightKey = `${right.schema}.${right.relation}`;
            return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
        })),
        organisationRowsWithinScope: z.literal(true),
        schemaUsage: z.boolean(),
        relationSelect: z.boolean(),
        publicSelect: z.literal(false),
        runtimeOwnerMembership: z.literal(false),
        verifiedAt: z.number().int().positive().safe(),
    }).strict(),
}).strict().superRefine((receipt, context) => {
    const expected = receipt.relation;
    const observed = receipt.verification;
    const expectedSources = expected.sourceRelations.map(({ schema, relation }) => `${schema}.${relation}`);
    const observedSources = observed.observedSourceRelations.map(({ schema, relation }) => `${schema}.${relation}`);
    if (!uniqueStrings(expectedSources) || !uniqueStrings(observedSources)
        || JSON.stringify(expectedSources) !== JSON.stringify(observedSources)
        || expected.definitionSha256 !== observed.observedDefinitionSha256
        || expected.columnSignatureSha256 !== observed.observedColumnSignatureSha256
        || expected.lineageSha256 !== observed.observedLineageSha256) {
        context.addIssue({
            code: "custom",
            path: ["verification"],
            message: "Warehouse visibility verification must match the exact relation authority.",
        });
    }
    const serving = receipt.transition === "activated";
    if ((receipt.visibility === "serving") !== serving
        || observed.relationSelect !== serving
        || (serving && !observed.schemaUsage)) {
        context.addIssue({
            code: "custom",
            path: ["visibility"],
            message: "Warehouse privileges must match the activation transition.",
        });
    }
});
export const twinLogicActivationEventV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-logic.activation-event.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    productId: z.string().regex(/^tl_prod_[0-9a-f]{32}$/u),
    productVersionId: z.string().regex(/^tl_pver_[0-9a-f]{32}$/u),
    installationId: z.string().regex(/^tl_inst_[0-9a-f]{32}$/u),
    idempotencyKey: boundedText(200),
    expectedPreviousActivationEventId: z.string()
        .regex(/^tl_act_[0-9a-f]{32}$/u).nullable(),
    kind: z.enum(["activated", "deactivated"]),
    warehouseVisibility: twinLogicWarehouseVisibilityReceiptV1Schema,
    reason: boundedText(4_000),
    actor: boundedText(200),
    createdAt: z.number().int().positive().safe(),
}).strict().superRefine((event, context) => {
    const visibility = event.warehouseVisibility;
    if (visibility.tenantSlug !== event.tenantSlug
        || visibility.organisationId !== event.organisationId
        || visibility.productId !== event.productId
        || visibility.productVersionId !== event.productVersionId
        || visibility.installationId !== event.installationId
        || visibility.transition !== event.kind
        || visibility.verification.verifiedAt > event.createdAt) {
        context.addIssue({
            code: "custom",
            path: ["warehouseVisibility"],
            message: "Activation event must bind its exact prior warehouse visibility verification.",
        });
    }
});
export function canonicalTwinLogicProductVersionJson(input) {
    return canonicalTwinBoundaryJson(twinLogicProductVersionV1Schema.parse(input));
}
export function canonicalTwinLogicAdmissionEvidenceJson(input) {
    return canonicalTwinBoundaryJson(twinLogicAdmissionEvidenceV1Schema.parse(input));
}
export function canonicalTwinLogicInstallationJson(input) {
    return canonicalTwinBoundaryJson(twinLogicInstallationV1Schema.parse(input));
}
export function canonicalTwinLogicWarehouseVisibilityJson(input) {
    return canonicalTwinBoundaryJson(twinLogicWarehouseVisibilityReceiptV1Schema.parse(input));
}
export function canonicalTwinLogicActivationEventJson(input) {
    return canonicalTwinBoundaryJson(twinLogicActivationEventV1Schema.parse(input));
}
